import { getAuth } from 'firebase/auth';
import { normalizeAgentMessage } from './agentMessageContract';

// WHATWG SSE framing is independent of network chunks and JSON payloads.
export function createSSEDecoder(onEvent) {
  let line = '';
  let data = [];
  let event = '';
  let id = '';
  let skipLF = false;
  const processLine = () => {
    if (!line) {
      if (data.length) onEvent({ event: event || 'message', data: data.join('\n'), id });
      data = [];
      event = '';
    } else if (!line.startsWith(':')) {
      const colon = line.indexOf(':');
      const field = colon < 0 ? line : line.slice(0, colon);
      let value = colon < 0 ? '' : line.slice(colon + 1);
      if (value.startsWith(' ')) value = value.slice(1);
      if (field === 'event') event = value;
      if (field === 'data') data.push(value);
      if (field === 'id' && !value.includes('\0')) id = value;
    }
    line = '';
  };
  return {
    push(text) {
      for (const character of text) {
        if (skipLF && character === '\n') {
          skipLF = false;
          continue;
        }
        skipLF = false;
        if (character === '\r' || character === '\n') {
          processLine();
          skipLF = character === '\r';
        } else line += character;
      }
    }
  };
}

function streamError(message, code, status) {
  return Object.assign(new Error(message), { code, status });
}

export function connectAgentStream(url, { method = 'GET', body, onOpen, onToken, onComplete, onError } = {}) {
  const controller = new AbortController();
  let reader;
  let terminal = false;
  let opened = false;
  let lastText = '';
  const seenIds = new Set();
  const toolStates = new Map();
  const open = (value) => {
    if (!opened) {
      opened = true;
      onOpen && onOpen(value);
    }
  };
  const complete = (info) => {
    if (terminal) return;
    terminal = true;
    onComplete && onComplete(info);
  };
  const fail = (error, payload) => {
    if (terminal) return;
    terminal = true;
    if (payload) {
      error.handled = true;
      onToken && onToken({ type: 'error', ...payload });
    }
    onError && onError(error);
  };
  const finishMessage = (message) => {
    if (!message || typeof message.content !== 'string') throw streamError('Invalid final message', 'invalid_event');
    const normalized = normalizeAgentMessage(message);
    lastText = normalized.content; // Empty final answers are authoritative too.
    const metadata = normalized.metadata || {};
    complete({
      status: 'completed',
      lastText,
      message: normalized,
      final: {
        message: lastText,
        message_id: normalized.id,
        follow_up_actions: metadata.follow_up_actions || [],
        tool_calls: metadata.tool_calls || [],
        reasoning_steps: metadata.reasoning_steps || [],
        reasoning_text: metadata.reasoning_summary || '',
        protocol_version: 2
      }
    });
  };
  const toolEvent = (value) => {
    if (!value.call_id || !value.tool_name) throw streamError('Tool event has no stable identity', 'invalid_event');
    const signature = JSON.stringify(value);
    if (toolStates.get(value.call_id) === signature) return;
    toolStates.set(value.call_id, signature);
    const common = {
      tool_name: value.tool_name,
      args: value.arguments,
      metadata: { tool_call_id: value.call_id, message_id: value.message_id, status: value.status }
    };
    if (value.type === 'tool.started' || value.status === 'running') {
      onToken && onToken({ type: 'tool_started', ...common });
    } else {
      onToken && onToken({ type: 'tool_output', ...common, result: value.result });
      onToken && onToken({ type: 'tool_completed', ...common, result: value.result, status: value.status });
    }
  };
  const receive = ({ event, data, id }) => {
    if (terminal || (id && seenIds.has(id))) return;
    if (id) seenIds.add(id);
    const value = JSON.parse(data);
    if (value.version != null && value.version !== 2) throw streamError('Unsupported agent protocol version', 'unsupported_protocol');
    if (value.version === 2) {
      if (value.type !== event || !value.run_id || !value.thread_id) throw streamError('Invalid agent event envelope', 'invalid_event');
      if (event === 'run.started') open({ ok: true, thread_id: value.thread_id, agent_run_id: value.run_id });
      else if (event === 'message.delta' || event === 'reasoning.delta') {
        if (typeof value.delta !== 'string') throw streamError('Invalid text delta', 'invalid_event');
        if (event === 'message.delta') lastText += value.delta;
        onToken && onToken({ type: event === 'message.delta' ? 'answer' : 'reasoning', text: value.delta });
      } else if (event === 'tool.started' || event === 'tool.completed') toolEvent(value);
      else if (event === 'run.snapshot') {
        if (typeof value.message?.content !== 'string') throw streamError('Invalid run snapshot', 'invalid_event');
        lastText = value.message.content;
        onToken && onToken({ type: 'message_snapshot', text: lastText, message: value.message });
        if (Array.isArray(value.tools)) value.tools.forEach(toolEvent);
      } else if (event === 'run.completed') finishMessage(value.message);
      else if (event === 'run.failed') {
        const payload = value.error || {};
        fail(streamError(payload.message || 'Agent request failed', payload.code || 'run_failed'), payload);
      } else if (event === 'run.cancelled') complete({ status: 'cancelled', lastText });
      return; // Heartbeats and future event types have no UI side effects.
    }
    // Rolling deployment compatibility. Only complete legacy JSON is adapted.
    if (event === 'open') open(value);
    else if (event === 'token' || event === 'message') {
      const text = value.text || value.delta || value.content || '';
      lastText += text;
      onToken && onToken({ type: 'answer', text });
    } else if (event === 'reasoning') onToken && onToken({ type: 'reasoning', text: value.text || value.delta || '' });
    else if (event === 'tool_started' || event === 'tool_output' || event === 'tool_completed')
      onToken && onToken({ ...value, type: event });
    else if (event === 'final') finishMessage({ id: value.message_id, content: value.message ?? '', metadata: value });
    else if (event === 'error') fail(streamError(value.message || 'Agent failed', value.code || 'run_failed'), value);
    else if (event === 'stopped') complete({ status: 'cancelled', lastText });
  };
  const done = (async () => {
    try {
      const user = getAuth().currentUser;
      if (!user) throw streamError('Not authenticated', 'not_authenticated');
      const token = await user.getIdToken();
      if (controller.signal.aborted) return;
      const response = await fetch(url, {
        method,
        credentials: 'include',
        signal: controller.signal,
        headers: { Accept: 'text/event-stream', Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {})
      });
      if (!response.ok) {
        let payload = {};
        try {
          payload = await response.json();
        } catch (_) {
          /* non-JSON HTTP error */
        }
        throw streamError(payload.error || payload.message || `Agent stream failed (${response.status})`, 'http_error', response.status);
      }
      const contentType = response.headers?.get?.('content-type');
      if (!response.body || (contentType && !contentType.includes('text/event-stream')))
        throw streamError('Expected an agent event stream', 'invalid_response');
      reader = response.body.getReader();
      const decoder = new TextDecoder();
      const parser = createSSEDecoder(receive);
      while (!terminal) {
        const { done: ended, value } = await reader.read();
        if (ended) {
          if (!terminal) throw streamError('Agent stream ended before a terminal event', 'incomplete_stream');
          break;
        }
        parser.push(decoder.decode(value, { stream: true }));
      }
    } catch (error) {
      if (error.name === 'AbortError' || controller.signal.aborted) complete({ status: 'cancelled', lastText });
      else fail(error);
    } finally {
      if (reader) {
        try {
          await reader.cancel();
        } catch (_) {
          /* closed reader */
        }
      }
    }
  })();
  return {
    done,
    close: () => {
      controller.abort();
      complete({ status: 'cancelled', lastText });
      if (reader) {
        try {
          reader.cancel().catch(() => {});
        } catch (_) {
          /* closed reader */
        }
      }
    }
  };
}
